import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, EmptyState, QueryStateBoundary, Select } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { useInvoices } from "@/api/hooks/useInvoices";
import { useEvent } from "@/api/hooks/useEvents";
import {
  INVOICE_KIND_BADGE_VARIANT,
  INVOICE_KIND_LABELS,
  INVOICE_STATUS_BADGE_VARIANT,
  INVOICE_STATUS_LABELS,
  InvoiceKindSchema,
  InvoiceStatusSchema,
  paidCents,
  type Invoice,
  type InvoiceKind,
  type InvoiceStatus,
} from "@/api/types/invoice";
import { formatShortDate } from "@/features/events/eventDate";

type StatusFilter = InvoiceStatus | "all";
type KindFilter = InvoiceKind | "all";

const STATUS_OPTIONS = InvoiceStatusSchema.options;
const KIND_OPTIONS = InvoiceKindSchema.options;

/**
 * Phase 5's invoice list across all events — filterable by status/kind.
 * Replaces the Phase 0-4 placeholder. Each row links to its event and to
 * the invoice detail page (InvoiceDetailPage) for line items, payments, and
 * status actions.
 */
export function MoneyPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInvoices({
    status: statusFilter === "all" ? undefined : statusFilter,
    kind: kindFilter === "all" ? undefined : kindFilter,
  });

  const invoices = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Money</h1>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="invoice-status-filter">
            Status
          </label>
          <Select
            id="invoice-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {INVOICE_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="invoice-kind-filter">
            Kind
          </label>
          <Select
            id="invoice-kind-filter"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
          >
            <option value="all">All kinds</option>
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {INVOICE_KIND_LABELS[k]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {invoices.length === 0 ? (
          <EmptyState
            icon="💰"
            title="No invoices yet"
            description="Deposit and balance invoices are drafted automatically when a quote is accepted."
            action={{ label: "Go to events", to: "/events" }}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-bg-card">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-bg-hover/50 text-xs uppercase tracking-wide text-text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Number</th>
                  <th className="px-4 py-2 font-medium">Event</th>
                  <th className="px-4 py-2 font-medium">Kind</th>
                  <th className="px-4 py-2 font-medium">Total</th>
                  <th className="px-4 py-2 font-medium">Paid</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invoices.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    onOpen={() => navigate(`/money/${invoice.id}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryStateBoundary>

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

interface InvoiceRowProps {
  invoice: Invoice;
  onOpen: () => void;
}

function InvoiceRow({ invoice, onOpen }: InvoiceRowProps) {
  const { data: event } = useEvent(invoice.event_id);

  return (
    <tr
      data-invoice-id={invoice.id}
      className="cursor-pointer hover:bg-bg-hover"
      onClick={onOpen}
    >
      <td className="px-4 py-3 font-medium text-text-primary">{invoice.number}</td>
      <td className="px-4 py-3 text-text-primary">
        {event ? `${formatShortDate(event.event_date)}${event.venue_name ? ` — ${event.venue_name}` : ""}` : "—"}
      </td>
      <td className="px-4 py-3">
        <Badge variant={INVOICE_KIND_BADGE_VARIANT[invoice.kind]}>{INVOICE_KIND_LABELS[invoice.kind]}</Badge>
      </td>
      <td className="px-4 py-3 text-text-secondary">{centsToUsd(invoice.total_cents)}</td>
      <td className="px-4 py-3 text-text-secondary">{centsToUsd(paidCents(invoice))}</td>
      <td className="px-4 py-3">
        <Badge variant={INVOICE_STATUS_BADGE_VARIANT[invoice.status]}>
          {INVOICE_STATUS_LABELS[invoice.status]}
        </Badge>
      </td>
    </tr>
  );
}
